import { create } from 'zustand';
import i18n from 'i18next';
import apiClient from '@/api/client';
import { interplayShellEnabled, SELF } from './config';
import { buildEtBarModel, type EtBarModel } from './etBarModel';
import type { InterplayShell } from './types';

/**
 * The ET bar's data for the signed-in person's current company. Held in a store (not React Query)
 * because App.tsx reads the brand accent above the QueryClientProvider. Never persisted.
 *
 * `GET /api/v1/interplay/shell`:
 * - 200 → the customer's shell (brand, launcher, return link).
 * - 404 → the default shell: this company, AnooreHR's own brand and words, only this product in
 *   the launcher, no return link (an account that isn't connected through Console).
 * - 503, any other error, or a malformed answer → no bar (the app looks as it always did).
 */
export type ShellResult =
  | { kind: 'shell'; shell: InterplayShell }
  | { kind: 'default' }
  | { kind: 'hide' };

const SELF_CODE = '__self__';

export function defaultShell(companyName: string | null | undefined): InterplayShell {
  return {
    object: 'interplay_shell',
    customer: { name: companyName || null, brand: null },
    account: { product_code: SELF_CODE },
    launcher: [{ product_code: SELF_CODE, product_name: SELF.name, state: 'active', account_name: companyName || null }],
    return_link: null,
    sector: null,
  };
}

export async function fetchInterplayShell(): Promise<ShellResult> {
  try {
    const res = await apiClient.get<InterplayShell>('/interplay/shell', {
      _silentOnError: true,
    } as Record<string, unknown>);
    return res.data && res.data.object === 'interplay_shell' ? { kind: 'shell', shell: res.data } : { kind: 'hide' };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return status === 404 ? { kind: 'default' } : { kind: 'hide' };
  }
}

interface ShellState {
  companyId: string | null;
  status: 'idle' | 'loading' | 'ready' | 'none';
  model: EtBarModel | null;
  /** The shell the model was built from (kept so the model is rebuilt on a language switch). */
  shell: InterplayShell | null;
  companyColour: string | null;
  /** Rebuild the model in the current language (the words come from i18n at build time). */
  retranslate: () => void;
  load: (companyId: string, companyName?: string | null, companyColour?: string | null) => Promise<void>;
  reset: () => void;
}

export const useInterplayShellStore = create<ShellState>((set, get) => ({
  companyId: null,
  status: 'idle',
  model: null,
  shell: null,
  companyColour: null,
  retranslate: () => {
    const { shell, companyColour } = get();
    if (shell) set({ model: buildEtBarModel(shell, { companyColour }) });
  },
  load: async (companyId: string, companyName?: string | null, companyColour?: string | null) => {
    if (!interplayShellEnabled()) return;
    const cur = get();
    if (cur.companyId === companyId && cur.status !== 'idle') return;
    set({ companyId, status: 'loading', model: null, shell: null });
    const result = await fetchInterplayShell();
    if (get().companyId !== companyId) return; // the person switched company meanwhile
    const shell =
      result.kind === 'shell' ? result.shell : result.kind === 'default' ? defaultShell(companyName) : null;
    const colour = companyColour ?? null;
    const model = shell ? buildEtBarModel(shell, { companyColour: colour }) : null;
    set({ status: model ? 'ready' : 'none', model, shell: model ? shell : null, companyColour: colour });
  },
  reset: () => set({ companyId: null, status: 'idle', model: null, shell: null, companyColour: null }),
}));

// Phase 3 follow-up (2026-10-26): the bar's words ("HR & Payroll", "Back to …") are built into the
// model from i18n, so a language switch rebuilds it from the kept shell.
i18n.on('languageChanged', () => useInterplayShellStore.getState().retranslate());
